<template>
  <div :class="[ 'm-range-slider', { 'm-range-slider--disabled': isDisabled } ]">
    <div
      class="input-container"
      :style="inputContainerStyle"
    >
      <HoverInput
        ref="minInput"
        :class="`text-${useAutoWidth ? 'center' : inputAlign}`"
        :style="hoverInputStyle"
        :init-value="minValue"
        @change="updateMinValueFromInput"
        :auto-width="useAutoWidth"
        :max-length="maxInputLength"
        :aria-label="ariaLabel && `${ariaLabel}, min`"
        :is-disabled="isDisabled"
      />
      <span
        v-if="inputSuffix"
        class="input-suffix"
        v-html="inputSuffix"
      />
    </div>
    <div
      class="slider-container"
      :style="sliderContainerStyle"
    >
      <VueSlider
        ref="slider"
        :model-value="[minValue, maxValue]"
        :min="min"
        :max="max"
        :dot-size="18"
        :height="5"
        :contained="true"
        :min-range="1"
        tooltip="none"
        :duration="0.2"
        @drag-end="emitValue"
        @dragging="onDragging"
        :use-keyboard="true"
        :keydown-hook="onKeyboardChange"
        :dot-attrs="ariaLabel ? { 'aria-label': ariaLabel } : {}"
        :disabled="isDisabled"
      />
    </div>
    <div
      class="input-container"
      :style="inputContainerStyle"
    >
      <HoverInput
        ref="maxInput"
        :class="[`text-${useAutoWidth ? 'center' : inputAlign}`]"
        :style="hoverInputStyle"
        :init-value="maxValue"
        @change="updateMaxValueFromInput"
        :auto-width="useAutoWidth"
        :max-length="maxInputLength"
        :aria-label="ariaLabel && `${ariaLabel}, max`"
        :is-disabled="isDisabled"
      />
      <span
        v-if="inputSuffix"
        class="input-suffix"
        v-html="inputSuffix"
      />
    </div>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'
import HoverInput from './../HoverInput/index.vue'

export default {
  name: 'FormRangeSlider',
  components: {
    HoverInput,
    VueSlider
  },
  props: {
    isDisabled: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      required: true
    },
    max: {
      type: Number,
      required: true
    },
    inputMin: {
      type: Number,
      default: undefined
    },
    inputMax: {
      type: Number,
      default: undefined
    },
    inputSuffix: {
      type: String,
      default: ''
    },
    inputAlign: {
      type: String,
      default: 'left'
    },
    inputWidth: {
      type: String,
      default: undefined
    },
    maxInputLength: {
      type: Number,
      default: undefined
    },
    sliderWidth: {
      type: String,
      default: '100px'
    },
    ariaLabel: {
      type: String,
      default: undefined
    }
  },
  data () {
    return {
      minValue: this.min,
      maxValue: this.max
    }
  },
  created () {
    if (this.inputMin !== undefined) {
      this.minValue = this.inputMin
    }
    if (this.inputMax !== undefined) {
      this.maxValue = this.inputMax
    }
  },
  computed: {
    useAutoWidth () {
      return !this.inputWidth
    },
    inputContainerStyle () {
      if (this.useAutoWidth) {
        const adjustement = this.inputSuffix ? this.inputSuffix.length - 1 : 1
        return {
          width: `${this.max.toString().length + adjustement}ch`
        }
      }
      return {}
    },
    hoverInputStyle () {
      if (this.inputWidth) {
        return { width: this.inputWidth }
      }
      return {}
    },
    sliderContainerStyle () {
      return {
        width: this.sliderWidth,
        ...(this.useAutoWidth && { 'margin-left': 0 })
      }
    }
  },
  methods: {
    valueHasChanged () {
      const value = this.$refs.slider.getValue()
      this.minValue = value[0]
      this.maxValue = value[1]
      this.updateInputMinValue()
      this.updateInputMaxValue()
    },
    updateInputMinValue () {
      if (this.$refs.minInput.getValue() !== this.minValue) {
        this.$refs.minInput.updateValue(this.minValue)
      }
    },
    updateInputMaxValue () {
      if (this.$refs.maxInput.getValue() !== this.maxValue) {
        this.$refs.maxInput.updateValue(this.maxValue)
      }
    },
    updateMinValueFromInput (value) {
      const number = parseInt(value)
      if (!isNaN(number)) {
        this.minValue = Math.min(Math.max(this.min, number), this.max)
        this.maxValue = Math.max(this.maxValue, this.minValue)
        this.updateInputMinValue()
        this.updateInputMaxValue()
        this.emitValue()
      }
    },
    updateMaxValueFromInput (value) {
      const number = parseInt(value)
      if (!isNaN(number)) {
        this.maxValue = Math.max(Math.min(this.max, number), this.min)
        this.minValue = Math.min(this.maxValue, this.minValue)
        this.updateInputMinValue()
        this.updateInputMaxValue()
        this.emitValue()
      }
    },
    onDragging () {
      this.valueHasChanged()
    },
    onKeyboardChange () {
      this.valueHasChanged()
      this.emitValue()
      return true // confirm that a change should be triggered
    },
    emitValue () {
      this.$emit('change', [this.minValue, this.maxValue])
    }
  },
  watch: {
    inputMin: {
      handler () {
        if (this.inputMin !== undefined) {
          this.minValue = this.inputMin
          this.$refs.minInput.updateValue(this.minValue)
          this.$refs.slider.setValue([this.minValue, this.maxValue])
        }
      }
    },
    inputMax: {
      handler () {
        if (this.inputMax !== undefined) {
          this.maxValue = this.inputMax
          this.$refs.maxInput.updateValue(this.maxValue)
          this.$refs.slider.setValue([this.minValue, this.maxValue])
        }
      }
    }
  }
}
</script>

<style lang="less">
@import '~vue-slider-component/theme/default.css';
@import '~vue-slider-component/dist-css/vue-slider-component.css';
@import (reference) "./../../../less/shared/utilities.less";
@import "@{frontend_static_less}/public/modules/m-range-slider.less";

.m-range-slider--disabled {
  filter: grayscale() opacity(40%);
}

.vue-slider-dot:focus-visible {
  outline: 2px solid @text-link-color
}
</style>
